<script setup>
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert } from 'dashboard/composables';
import MessageApi from 'dashboard/api/inbox/message';
import BaseBubble from './Base.vue';
import MessageMeta from '../MessageMeta.vue';
import Icon from 'next/icon/Icon.vue';
import AudioChip from 'next/message/chips/Audio.vue';
import { useMessageContext } from '../provider.js';
import { MESSAGE_VARIANTS } from '../constants';

const {
  attachments,
  shouldGroupWithNext,
  variant,
  sender,
  id,
  conversationId,
} = useMessageContext();

const { t } = useI18n();
const { currentAccount } = useAccount();
const isTranscribing = ref(false);

const attachment = computed(() => {
  return attachments.value[0];
});

// Offered only where it can do something: the account has the feature on and this voice note has
// no text yet. In automatic mode it therefore shows up only on the ones that failed.
const canTranscribe = computed(
  () =>
    !!currentAccount.value?.settings?.audio_transcriptions &&
    !attachment.value?.transcribedText
);

const transcribe = async () => {
  isTranscribing.value = true;
  try {
    await MessageApi.transcribeMessage(conversationId.value, id.value);
    useAlert(t('CONVERSATION.AUDIO_PLAYER.TRANSCRIBE_REQUESTED'));
  } catch (error) {
    useAlert(
      error?.response?.data?.error ||
        t('CONVERSATION.AUDIO_PLAYER.TRANSCRIBE_ERROR')
    );
  } finally {
    isTranscribing.value = false;
  }
};

const senderName = computed(() => sender.value?.name ?? '');
const senderAvatarUrl = computed(
  () => sender.value?.thumbnail || sender.value?.avatarUrl || ''
);

// Like WhatsApp, the timestamp shares the line under the waveform with the
// audio duration instead of taking a line of its own below the player.
const metaColorClass = computed(() =>
  variant.value === MESSAGE_VARIANTS.PRIVATE
    ? 'text-n-amber-12/50'
    : 'text-n-slate-11'
);
</script>

<template>
  <BaseBubble class="p-2.5" data-bubble-name="audio" hide-meta>
    <AudioChip
      :attachment="attachment"
      :sender-name="senderName"
      :sender-avatar-url="senderAvatarUrl"
      plain
      class="skip-context-menu"
    >
      <template #meta>
        <MessageMeta v-if="!shouldGroupWithNext" :class="metaColorClass" />
        <button
          v-if="canTranscribe"
          class="grid p-0 bg-transparent border-0 shrink-0 size-5 place-content-center disabled:opacity-50"
          :disabled="isTranscribing"
          :aria-label="t('CONVERSATION.AUDIO_PLAYER.TRANSCRIBE')"
          :title="t('CONVERSATION.AUDIO_PLAYER.TRANSCRIBE')"
          @click="transcribe"
        >
          <Icon class="size-4" icon="i-lucide-captions" />
        </button>
      </template>
    </AudioChip>
  </BaseBubble>
</template>
